import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, Banknote, Leaf, Sparkles } from "lucide-react";
import SparksCanvas from "./SparksCanvas";
import { scrollToId } from "../lib/api";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain">
      <SparksCanvas className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,6,11,0.7)_100%)]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8 pt-32 pb-20 w-full grid lg:grid-cols-12 gap-14 items-center"
      >
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3 font-mono text-[11px] sm:text-xs tracking-[0.34em] uppercase text-ember"
          >
            <span className="h-px w-10 bg-ember/60" />
            Authentic Fireworks
          </motion.p>

          <h1 className="mt-6 font-display font-black uppercase leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
            <span className="block overflow-hidden">
              <motion.span variants={lineAnim} custom={0} initial="hidden" animate="show" className="block">
                Light up
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={lineAnim} custom={1} initial="hidden" animate="show" className="block text-outline">
                the night,
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                variants={lineAnim}
                custom={2}
                initial="hidden"
                animate="show"
                className="block font-editorial italic font-medium normal-case text-ember text-4xl sm:text-5xl lg:text-6xl xl:text-7xl"
              >
                like never before.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-muted-night text-base sm:text-lg leading-relaxed"
          >
            From single sparklers to 60-shot sky shows — 50+ hand-picked crackers packed
            fresh and delivered home, <span className="text-ember font-semibold">cash on delivery</span>. No prepay, no hassle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-shop-cta"
              onClick={() => scrollToId("shop")}
              className="group relative inline-flex items-center gap-3 rounded-full bg-ember px-8 py-4 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian transition-all duration-300 hover:bg-marigold hover:scale-[1.03] ember-glow"
            >
              <Sparkles className="h-4 w-4" />
              Shop the collection
            </button>
            <button
              data-testid="hero-delivery-cta"
              onClick={() => scrollToId("delivery")}
              className="inline-flex items-center gap-3 rounded-full border border-starlight/20 px-8 py-4 font-mono text-xs tracking-[0.22em] uppercase text-starlight transition-all duration-300 hover:border-ember/70 hover:text-ember"
            >
              How we deliver
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] tracking-[0.18em] uppercase text-muted-night"
          >
            <span className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-ember" /> 100% Original Quality
            </span>
            <span className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-ember" /> Cash on Delivery
            </span>
            <span className="flex items-center gap-2">
              <Leaf className="h-4 w-4 text-ember" /> Eco Green Crackers
            </span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imgY }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative clipped-frame rounded-3xl overflow-hidden ember-glow"
            data-testid="hero-visual"
          >
            <img
              src="/images/hero-sparkler.jpeg"
              alt="Hand holding a lit golden sparkler in the dark"
              className="w-full h-[420px] sm:h-[520px] lg:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="absolute -top-5 -right-3 sm:-right-6 animate-floaty rounded-2xl border border-ember/40 bg-obsidian/90 backdrop-blur px-5 py-3.5"
          >
            <p className="font-mono text-sm font-bold tracking-[0.16em] uppercase text-ember">Cash on</p>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-night">Delivery</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.45 }}
            className="absolute -bottom-5 left-6 rounded-2xl border border-starlight/15 bg-obsidian/90 backdrop-blur px-5 py-3"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-starlight">Fresh 2025 stock</p>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
